/**
 * The wait, in Or's design. Same state machine as `WaitingStep`: the same
 * branches, the same titles, the same three-attempt budget, and the same
 * ban on a percentage or a promised completion time.
 */

import React, { useState } from 'react';
import MockPanel from './MockPanel';
import { copyFor, SHARED } from '../audienceCopy';

function Question({ question, onAnswer, onSkip }) {
  const [selected, setSelected] = useState([]);
  const [other, setOther] = useState('');
  const toggle = (choice) => setSelected((prev) => (
    prev.includes(choice) ? prev.filter((c) => c !== choice) : [...prev, choice]
  ));

  return (
    <div className="mk-card">
      <p className="mk-eyebrow">While we look</p>
      <h2 className="mk-h3">{question.question}</h2>
      <p className="mk-help">Choose any that fit, or skip.</p>
      <div className="mk-choices">
        {question.choices.map((choice) => (
          <label className="mk-choice" key={choice}>
            {/* A real checkbox in both designs — the tests pin it. */}
            <input type="checkbox" checked={selected.includes(choice)} onChange={() => toggle(choice)} />
            {choice}
          </label>
        ))}
      </div>
      {selected.includes('Other') && (
        <div style={{ marginTop: 12 }}>
          <input
            className="mk-input"
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder={SHARED.otherFieldLabel}
            aria-label={SHARED.otherFieldLabel}
          />
        </div>
      )}
      <div className="mk-actions">
        <button type="button" className="mk-btn mk-btn--ghost" onClick={onSkip}>Skip</button>
        <button
          type="button"
          className="mk-btn mk-btn--primary"
          disabled={selected.length === 0}
          onClick={() => onAnswer({ key: question.key, question: question.question, choices: selected, otherText: other })}
        >
          Next
        </button>
      </div>
    </div>
  );
}

export default function MockWaiting({
  audience, brief, state, questionIndex, onAnswer, onSkip, onOpenList, onRetry, retrying,
  attemptsRemaining = 2,
  onRevise,
}) {
  const copy = copyFor(audience);
  const questions = copy.questions;
  const question = questions[questionIndex] || null;

  const ready = state === 'ready';
  const empty = state === 'empty';
  const delayed = state === 'delayed';
  const failed = state === 'failed';
  const exhausted = attemptsRemaining <= 0;
  const stalled = delayed || failed;
  const busy = !ready && !empty && !stalled;
  // Nothing is left to read or do in the left column: no question
  // outstanding, no empty-result notice, no retry. True while the search
  // runs past the last question AND once it has landed, which is the state
  // in both screenshots of the problem.
  const solo = !(empty || stalled || (busy && question));

  const title = empty ? copy.firstListEmptyTitle
    : exhausted && stalled ? SHARED.listFailedFinalTitle
      : delayed ? SHARED.listDelayed
        : failed ? SHARED.firstListFailedTitle
          : copy.firstListWait;

  return (
    <>
      <p className="mk-eyebrow">Your first list</p>
      <h1 className="mk-h1 mk-h1--sm">{title}</h1>
      {!empty && !stalled && <p className="mk-lede">{copy.firstListWaitSupport}</p>}

      {/* Above the columns, not inside the left one: it is a recap of what
          was asked for, and when the left column collapses it must not go
          with it. */}
      <p className="mk-brief-echo">Your brief: {brief}</p>

      {/* With no question outstanding there is nothing in the left column
          at all, and a 482px panel pinned to the right of an empty half
          screen reads as a layout that broke. `--solo` collapses the left
          column and the panel slides to the middle; the width transition
          is what makes that a move rather than a jump. */}

      <div className={`mk-stage${solo ? ' mk-stage--solo' : ''}`}>
        <div className="mk-stage-left">
          {empty && <div className="mk-notice">{SHARED.firstListEmpty}</div>}
          {empty && onRevise && (
            <div className="mk-actions">
              <button type="button" className="mk-btn mk-btn--primary" onClick={onRevise}>
                {copy.primaryAction}
              </button>
            </div>
          )}
          {stalled && (
            <div className="mk-notice">
              {exhausted ? SHARED.listFailedFinalBody : SHARED.firstListFailed}
            </div>
          )}
          {stalled && !exhausted && (
            <div className="mk-actions">
              <button type="button" className="mk-btn mk-btn--primary" onClick={onRetry} disabled={retrying}>
                {SHARED.tryAgain}
              </button>
            </div>
          )}

          {busy && question && (
            // `key`, and it is load-bearing. Without it React reuses the
            // same instance for every question, so `selected` and `other`
            // survive the move to the next one. The new question's boxes
            // render unchecked — none of its labels match the stale values
            // — but those values are still in state and were SAVED with the
            // next answer, so question 2 recorded question 1's choice and
            // question 3 recorded both. It also left Next enabled before
            // anything had been ticked. The other design has always had it.
            <Question key={question.key} question={question} onAnswer={onAnswer} onSkip={onSkip} />
          )}
        </div>

        <MockPanel
          busy={busy}
          working={SHARED.panelFindingList}
          title={ready ? copy.firstListReady : empty ? 'Nothing to show' : exhausted ? 'Stopped' : stalled ? 'Still working' : null}
          sub={ready ? 'Explore it now.' : empty ? SHARED.firstListEmptySub : null}
          action={ready ? (
            <button type="button" className="mk-btn mk-btn--primary" onClick={onOpenList}>
              {SHARED.openList}
            </button>
          ) : null}
        />
      </div>
    </>
  );
}
